import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  ViewChild,
  model,
  Output,
} from "@angular/core";
import { DxButtonModule, DxPopupComponent, DxPopupModule } from "devextreme-angular";
import { GpdButtonComponent } from "../gpd-button/gpd-button.component";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import { IconName } from "src/app/helpers/icon-exports";

export interface PopupButton {
  text: string;
  onClick: () => void;
  type?: "primary" | "secondary" | "tertiary" | "tertiary-text" | "danger" | "danger-outline" | "text";
  submitBehavior?: boolean;
  icon?: string;
  disabled?: boolean;
  /**
   * When false, clicking the button runs its handler without auto-closing the popup.
   * Use for buttons whose handler is async (e.g. an upload) and that should keep the
   * popup open until the work completes and the caller closes it explicitly. Defaults to true.
   */
  closeOnClick?: boolean;
}

@Component({
    selector: "app-generic-popup",
    imports: [CommonModule, DxPopupModule, DxButtonModule, GpdButtonComponent, SvgIconComponent],
    templateUrl: "./generic-popup.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./generic-popup.component.css"
})
export class GenericPopupComponent implements OnDestroy {
  visible = model(false);
  height = model("auto");
  width = model("auto");
  @Output() onShowing: EventEmitter<void> = new EventEmitter<void>();
  @Output() onHiding: EventEmitter<void> = new EventEmitter<void>();
  allowOutsideClick = model(true);

  @Input() title: string;
  @Input() titleIcon?: IconName;
  @Input() content: string;
  // Default cap for height="auto" callers: without it a growing body (e.g. task notes, #4924)
  // pushes the popup past the viewport, and .gpd-popup-body only scrolls once the popup is bounded.
  @Input() maxHeight?: string | number = "90vh";
  /** Never wider than the window, whatever fixed width a caller passes. */
  @Input() maxWidth?: string | number = "95vw";
  /** Fixed-height popups whose content fills the body and scrolls itself (see .gpd-popup-fill). */
  @Input() fill = false;

  @Input() leftButtons: PopupButton[] = [];

  @Input() buttons: PopupButton[] = [
    {
      text: $localize`:@@cancel:Cancel`,
      onClick: () => {
        this.visible.set(false);
      },
      type: "secondary",
    },
    {
      text: $localize`:@@ok:OK`,
      onClick: () => {
        this.visible.set(false);
      },
    },
  ];

  @ViewChild("popupContent") popupContent: ElementRef;
  @ViewChild("popupBody") private popupBody?: ElementRef<HTMLElement>;
  @ViewChild(DxPopupComponent) private dxPopup?: DxPopupComponent;

  private bodyResizeObserver?: ResizeObserver;

  onShowingHandler() {
    this.onShowing.emit();
    // Focus the popup content when shown
    setTimeout(() => {
      this.popupContent?.nativeElement?.focus();
      this.startObservingBodySize();
    });
  }
  onHidingHandler() {
    this.onHiding.emit();
    this.stopObservingBodySize();
  }

  ngOnDestroy(): void {
    this.stopObservingBodySize();
  }

  // dx-popup centres itself on open but keeps its top anchored when the content height later
  // changes (e.g. a form that swaps field sets), so it drifts off-centre. Re-centre on resize.
  private startObservingBodySize(): void {
    this.stopObservingBodySize();
    const el = this.popupBody?.nativeElement;
    if (!el || typeof ResizeObserver === "undefined") return;

    let lastHeight = 0;
    this.bodyResizeObserver = new ResizeObserver(entries => {
      const height = Math.round(entries[0].contentRect.height);
      if (height === lastHeight) return;
      lastHeight = height;
      this.dxPopup?.instance?.repaint();
    });
    this.bodyResizeObserver.observe(el);
  }

  private stopObservingBodySize(): void {
    this.bodyResizeObserver?.disconnect();
    this.bodyResizeObserver = undefined;
  }

  onEnter() {
    this.buttons.find((button) => button.submitBehavior === true)?.onClick?.();
  }

  onButtonClick(button: PopupButton) {
    button.onClick();
    if (button.closeOnClick !== false) {
      this.visible.set(false);
    }
  }
}
